import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  IndianRupee, Briefcase, FileText, TrendingUp, Search, Plus, Download,
  Star, User, CheckCircle, AlertCircle, Check, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CorporateDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [pendingMilestones, setPendingMilestones] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [freelancers, setFreelancers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '', description: '', category: '', budget_min: '', budget_max: '', skills_required: '', deadline: '',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, contractsRes, milestonesRes, walletRes] = await Promise.all([
        axios.get(`${API}/corporate/stats`),
        axios.get(`${API}/contracts`),
        axios.get(`${API}/client/milestones/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/wallet`).catch(() => ({ data: { balance: 0 } })),
      ]);
      setStats(statsRes.data);
      setContracts(contractsRes.data.filter(c => c.client_id === user.id));
      setPendingMilestones(milestonesRes.data);
      setWalletBalance(walletRes.data?.balance || 0);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (term) => {
    if (!term.trim()) { setFreelancers([]); return; }
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/corporate/freelancers`, { params: { search: term } });
      setFreelancers(res.data);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => handleSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm, handleSearch]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, {
        ...newProject,
        budget_min: parseFloat(newProject.budget_min),
        budget_max: parseFloat(newProject.budget_max),
        skills_required: newProject.skills_required.split(',').map(s => s.trim()),
      });
      toast.success('Project posted!');
      setShowProjectDialog(false);
      setNewProject({ title: '', description: '', category: '', budget_min: '', budget_max: '', skills_required: '', deadline: '' });
      fetchData();
    } catch { toast.error('Failed to post project'); }
  };

  const handleApproveMilestone = async (milestoneId) => {
    try {
      await axios.patch(`${API}/milestones/${milestoneId}/client-approve`);
      toast.success('Milestone approved!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDownloadInvoice = (contractId) => {
    window.open(`${API}/invoices/${contractId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96"><div className="text-lg">Loading...</div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2" data-testid="dashboard-title">
            Corporate Dashboard
          </h1>
          <p className="text-muted-foreground" data-testid="dashboard-subtitle">Manage enterprise hiring and track team performance</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card data-testid="stat-projects">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Projects</CardTitle>
              <FileText className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{stats?.total_projects || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-wallet">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Wallet</CardTitle>
              <IndianRupee className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-green-600">₹{walletBalance.toLocaleString('en-IN')}</div>
              <button onClick={() => navigate('/wallet')} className="text-xs text-secondary hover:underline mt-1">View wallet</button>
            </CardContent>
          </Card>
          <Card data-testid="stat-active">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <Briefcase className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{stats?.active_contracts || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-completed">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <TrendingUp className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{stats?.completed_contracts || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-spent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
              <IndianRupee className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">₹{((stats?.total_spent || 0) / 1000).toFixed(0)}K</div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Milestone Approvals */}
        {pendingMilestones.length > 0 && (
          <Card className="mb-8 border-amber-200 bg-amber-50" data-testid="pending-milestones-section">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-lg font-heading text-amber-800">Pending Approvals ({pendingMilestones.length})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingMilestones.map(ms => (
                  <div key={ms.id} className="flex items-center justify-between bg-white rounded-lg p-4 border border-amber-100" data-testid={`pending-milestone-${ms.id}`}>
                    <div>
                      <p className="font-medium text-sm">{ms.title}</p>
                      <p className="text-xs text-muted-foreground">{ms.freelancer_name} · ₹{ms.amount.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproveMilestone(ms.id)} data-testid={`approve-ms-${ms.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${ms.contract_id}`)}>View</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowProjectDialog(true)} data-testid="post-project-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Post a Project</CardTitle>
              <CardDescription>Get competitive bids from freelancers</CardDescription>
            </CardHeader>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/gigs')} data-testid="browse-gigs-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Browse Gigs</CardTitle>
              <CardDescription>Hire directly from the gig marketplace</CardDescription>
            </CardHeader>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices')} data-testid="invoices-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">GST Invoices</CardTitle>
              <CardDescription>Download PDF invoices for all contracts</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Post Project Dialog */}
        <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Post New Project</DialogTitle>
              <DialogDescription>Get competitive bids from freelancers</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-4" data-testid="create-project-form">
              <div>
                <Label>Project Title</Label>
                <Input value={newProject.title} onChange={e => setNewProject({...newProject, title: e.target.value})} required data-testid="project-title-input" placeholder="Build a mobile app" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} required rows={3} data-testid="project-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Input value={newProject.category} onChange={e => setNewProject({...newProject, category: e.target.value})} required data-testid="project-category-input" />
                </div>
                <div>
                  <Label>Deadline (optional)</Label>
                  <Input type="date" value={newProject.deadline} onChange={e => setNewProject({...newProject, deadline: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Budget (₹)</Label>
                  <Input type="number" value={newProject.budget_min} onChange={e => setNewProject({...newProject, budget_min: e.target.value})} required data-testid="project-budget-min-input" />
                </div>
                <div>
                  <Label>Max Budget (₹)</Label>
                  <Input type="number" value={newProject.budget_max} onChange={e => setNewProject({...newProject, budget_max: e.target.value})} required data-testid="project-budget-max-input" />
                </div>
              </div>
              <div>
                <Label>Skills Required (comma separated)</Label>
                <Input value={newProject.skills_required} onChange={e => setNewProject({...newProject, skills_required: e.target.value})} required placeholder="React, Node.js" data-testid="project-skills-input" />
              </div>
              <Button type="submit" className="w-full">Post Project</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Freelancer Search */}
        <Card className="mb-8" data-testid="freelancer-section">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl font-heading">Find Freelancers</CardTitle>
                <CardDescription>Search verified talent for your team</CardDescription>
              </div>
              <Button onClick={() => navigate('/gigs')} variant="outline" data-testid="browse-all-button">Browse All Gigs</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, bio, or skills..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="freelancer-search"
              />
            </div>
            {searchLoading && <p className="text-center text-muted-foreground py-4 text-sm">Searching...</p>}
            {!searchLoading && searchTerm && freelancers.length === 0 && (
              <p className="text-center text-muted-foreground py-4 text-sm" data-testid="no-freelancers-message">No freelancers found matching "{searchTerm}"</p>
            )}
            {!searchLoading && !searchTerm && (
              <p className="text-center text-muted-foreground py-6 text-sm">Type a name or skill to search freelancers</p>
            )}
            {freelancers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="freelancer-results">
                {freelancers.slice(0, 6).map(fl => (
                  <div key={fl.id} className="flex items-start gap-3 border rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/profile/${fl.id}`)} data-testid={`freelancer-${fl.id}`}>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {fl.avatar_url
                        ? <img src={fl.avatar_url} alt={fl.full_name} className="w-10 h-10 rounded-full object-cover" />
                        : <User className="w-5 h-5 text-primary" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{fl.full_name}</p>
                        {fl.rating > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-amber-500">
                            <Star className="w-3 h-3 fill-amber-400" /> {fl.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{fl.bio || 'No bio available'}</p>
                      {fl.skills?.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {fl.skills.slice(0, 3).map((s, i) => <Badge key={i} variant="secondary" className="text-xs py-0">{s}</Badge>)}
                        </div>
                      )}
                    </div>
                    {fl.hourly_rate && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-medium text-secondary">₹{fl.hourly_rate}/hr</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Contracts */}
        <Card data-testid="contracts-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Work & Payments</CardTitle>
            <CardDescription>Monitor ongoing contracts and payment status</CardDescription>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <p className="text-muted-foreground text-center py-8" data-testid="no-contracts-message">No active contracts. Start hiring to build your team!</p>
            ) : (
              <div className="space-y-3">
                {contracts.map(contract => {
                  const paidPct = contract.amount > 0 ? Math.min(100, ((contract.total_milestones_paid || 0) / contract.amount) * 100) : 0;
                  return (
                    <div key={contract.id} className="border rounded-lg p-4" data-testid={`contract-${contract.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-semibold">{contract.freelancer_name}</h3>
                            <Badge variant={contract.status === 'active' ? 'default' : 'secondary'}>{contract.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{contract.type === 'gig' ? 'Gig Order' : 'Project Contract'}</p>
                        </div>
                        <div className="text-right">
                          <div className="font-heading font-bold text-secondary">₹{contract.amount.toLocaleString('en-IN')}</div>
                          <Badge variant="outline" className="text-xs">{contract.payment_status}</Badge>
                        </div>
                      </div>
                      {contract.milestones_enabled && (
                        <div className="mb-2">
                          <Progress value={paidPct} className="h-1.5" />
                          <p className="text-xs text-muted-foreground mt-1">₹{(contract.total_milestones_paid || 0).toLocaleString('en-IN')} released</p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${contract.id}`)} data-testid={`view-contract-${contract.id}`}>View</Button>
                        {['escrowed', 'released'].includes(contract.payment_status) && (
                          <Button size="sm" variant="outline" onClick={() => handleDownloadInvoice(contract.id)} data-testid={`download-invoice-${contract.id}`}>
                            <Download className="w-3 h-3 mr-1" /> Invoice
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CorporateDashboard;
